import React from 'react';
import clsx from 'clsx';
import { TentTypeV1, TentTypeV2 } from '@camp67/model';
import { trackButtonClick, ButtonTag } from '../../state/analytics';

export interface TentCarouselItem {
  tentType: TentTypeV1 | TentTypeV2;
  imageUrl: string;
  imageAlt: string;
  title: string;
}

export interface TentExperienceCarouselMessages {
  roomViewer: string;
}

export interface TentExperienceCarouselProps {
  tents: TentCarouselItem[];
  activeTent: TentTypeV1 | TentTypeV2;
  onTentChange: (type: TentTypeV1 | TentTypeV2) => void;
  countProvider?: (type: TentTypeV1 | TentTypeV2) => number;
  messages: TentExperienceCarouselMessages;
}

export const TentExperienceCarousel: React.FC<TentExperienceCarouselProps> = ({
  tents,
  activeTent,
  countProvider,
  onTentChange,
  messages,
}) => {
  const activeIndex = tents.findIndex((t) => t.tentType === activeTent);
  const activeTentItem = tents[activeIndex];

  const onPrev = () => {
    const prevIndex = Math.max(0, activeIndex - 1);
    onTentChange(tents[prevIndex].tentType);
    trackButtonClick(ButtonTag.PREV_ROOM);
  };

  const onNext = () => {
    const nextIndex = (activeIndex + 1) % tents.length;
    onTentChange(tents[nextIndex].tentType);
    trackButtonClick(ButtonTag.NEXT_ROOM);
  };

  return (
    <div className="flex flex-col-reverse w-full">
      {/* Thumbnail strip */}
      <div className="flex w-full">
        {tents.map((tent, key) => {
          const count = countProvider?.(tent.tentType);
          const isActive = activeTent === tent.tentType;

          return (
            <button
              onClick={() => onTentChange(tent.tentType)}
              key={key}
              type="button"
              className="relative flex max-w-[400px] h-16 w-1/5 sm:min-h-24 md:min-h-28"
            >
              <img
                src={tent.imageUrl}
                alt={tent.imageAlt}
                className={clsx(
                  'w-full pointer-events-none select-none overflow-x-clip h-full object-cover',
                  isActive ? 'opacity-100' : 'opacity-50',
                )}
              />
              <span className="hidden sm:block bg-white absolute left-1/2 -translate-x-1/2 bottom-4 px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-medium whitespace-nowrap">
                {tent.title}
              </span>
              {count && count > 0 ? (
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white bg-black h-8 w-8 rounded-full flex items-center justify-center">
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Navigation header */}
      <div className="bg-black w-full flex justify-between px-5">
        <button onClick={onPrev} type="button" className="text-white">
          &larr;
        </button>
        <p className="text-white text-center py-3">
          <span className="uppercase tracking-widest text-xs block">
            {messages.roomViewer}
          </span>
          <span className="font-bold">{activeTentItem?.title}</span>
        </p>
        <button onClick={onNext} type="button" className="text-white">
          &rarr;
        </button>
      </div>
    </div>
  );
};
